import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { baseurl } from "../../Baseurl";
import EventBus from "../../Context/EventBus";
import { NavLink, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../api/axiosInstance";

import Select from "react-select";
import { fetchModuleMappings, hasSubModulePermission, isAdmin } from "../../utils/permissions";
import dayjs from "dayjs";

export default function Billing() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const user = useSelector((state) => state.user.user);
  const allBranchSettings = useSelector((state) => state.business.allBranchSettings);
  const activeBranchId = useSelector((state) => state.business.activeBranchId);
  const [openmodal, setOpenmodal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // New-invoice modal state
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [services, setServices] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [selected, setSelected] = useState({ services: {}, inventory: {} });
  const [servicesLoading, setServicesLoading] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [invoicePage, setInvoicePage] = useState(1);
  const itemsPerPage = 10;
  // Payments tab + state
  const [activeTab, setActiveTab] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentPage, setPaymentPage] = useState(1);
  const [paySearch, setPaySearch] = useState("");
  const [payStartDate, setPayStartDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [payEndDate, setPayEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [payCashierFilter, setPayCashierFilter] = useState("");
  const [payBranchFilter, setPayBranchFilter] = useState("");
  const [payMethodFilter, setPayMethodFilter] = useState("");
  const [invoiceStartDate, setInvoiceStartDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [invoiceEndDate, setInvoiceEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [invoiceStartTime, setInvoiceStartTime] = useState("");
  const [invoiceEndTime, setInvoiceEndTime] = useState("");
  const [totalAmount, setTotalAmount] = useState(0);

  const [permissions, setPermissions] = useState({
    invoices: false,
    paymentInvoices: false
  });

  useEffect(() => {
    const checkPermissions = async () => {
      if (isAdmin()) {
        setPermissions({ invoices: true, paymentInvoices: true });
        return;
      }

      try {
        const allModules = await fetchModuleMappings();
        const billingModule = allModules.find(m => m.module_name.toLowerCase() === 'billing');

        if (billingModule && billingModule.sub_modules) {
          const invoicesSub = billingModule.sub_modules.find(sm => sm.sub_module_name === 'Invoices');
          const paymentInvoicesSub = billingModule.sub_modules.find(sm => sm.sub_module_name === 'Payment_Invoices');

          setPermissions({
            invoices: invoicesSub ? hasSubModulePermission(invoicesSub.id) : false,
            paymentInvoices: paymentInvoicesSub ? hasSubModulePermission(paymentInvoicesSub.id) : false
          });
        }
      } catch (err) {
        console.error("Error checking permissions", err);
      }
    };

    checkPermissions();
  }, []);

  // Set active tab based on permissions
  useEffect(() => {
    // Only set the tab if it hasn't been set yet (null)
    if (activeTab === null) {
      if (permissions.invoices) {
        setActiveTab("invoices");
      } else if (permissions.paymentInvoices) {
        setActiveTab("payments");
      }
    }
  }, [permissions, activeTab]);

  // Insurance company modal state
  const [insOpen, setInsOpen] = useState(false);
  const [insLoading, setInsLoading] = useState(false);
  const [insForm, setInsForm] = useState({
    company_name: "",
    address: "",
    phone_number: "",
    email: "",
    website: "",
    company_logo: null,
  });
  const [insListOpen, setInsListOpen] = useState(false);
  const [insListLoading, setInsListLoading] = useState(false);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);

  // Branch selectors for invoice/payment modals
  const [invoiceBranchId, setInvoiceBranchId] = useState(null);
  const [payBranchId, setPayBranchId] = useState(null);

  // Payment modal state (for Payments tab)
  const [payOpen, setPayOpen] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [payPatientId, setPayPatientId] = useState("");
  const [remainingInvoices, setRemainingInvoices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [payForm, setPayForm] = useState({
    invoice_id: "",
    amount: "",
    payment_method: "",
    transaction_id: "",
  });

  // Searchable Patient dropdown helpers 
  const patientOptions = patients.map((p) => ({
    value: String(p.id),
    label: `${(p.firstName || "").trim()} ${(p.lastName || "").trim()}`.trim(),
    mobileNumber: p.phone || p.mobileNumber || "",
    civilIdNumber: p.civilIdNumber || p.patient_civil_id || "",
    email: p.email || "",
    age: p.age || "",
  }));

  const customFilterOption = (option, inputValue) => {
    const searchValue = (inputValue || "").toLowerCase();
    const data = option.data || {};
    return (
      (data.label || "").toLowerCase().includes(searchValue) ||
      (data.mobileNumber || "").toLowerCase().includes(searchValue) ||
      (data.civilIdNumber || "").toLowerCase().includes(searchValue) ||
      (data.email || "").toLowerCase().includes(searchValue) ||
      String(data.age || "").toLowerCase().includes(searchValue)
    );
  };

  // Load invoices from API
  useEffect(() => {
    loadInvoices();
    loadPayments();
    loadPaymentMethods();
  }, []);

  useEffect(()=>{
    loadInvoices();
  },[invoiceStartDate, invoiceEndDate]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const params = {};

      if(invoiceStartDate) {
        params.startDate = invoiceStartDate;
      }
      if(invoiceEndDate) {
        params.endDate = invoiceEndDate;
      }


      const res = await axiosInstance.get(`getAllInvoices`, {params});
      const list = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
      setInvoices(list);
      setTotalAmount(res.data?.total_amount || 0);
    } catch (error) {
      console.error("Error loading invoices:", error);

      
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      setPaymentsLoading(true);
      const res = await axiosInstance.get(`getAllPayments`);
      const list = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
      setPayments(list);
    } catch (error) {
      console.error("Error loading payments:", error);
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const res = await axiosInstance.get(`getAllPaymentMethods`);
      const list = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
      setPaymentMethods(list);
      // Set default payment method to first one if available
      if (list.length > 0) {
        setPayForm(prev => ({ ...prev, payment_method: list[0].name }));
      }
    } catch (error) {
      console.error("Error loading payment methods:", error);
      // Fallback to default methods
      setPaymentMethods([
        { id: 1, name: "Cash" },
        { id: 2, name: "Card" },
        { id: 3, name: "Transfer" },
        { id: 4, name: "Cheque" }
      ]);
      setPayForm(prev => ({ ...prev, payment_method: "Cash" }));
    }
  };

  // Get logged-in user name from Redux
  const getLoggedInUserName = () => {
    return user?.fullName || user?.shortName || user?.name || user?.username || 'User';
  };

  // Open/close payment modal
  const openPaymentModal = async () => {
    setPayOpen(true);
    setPayBranchId(activeBranchId);
    if (!patients.length) {
      await loadPatients();
    }
    setPayPatientId("");
    setRemainingInvoices([]);
    setPayForm({
      invoice_id: "",
      amount: "",
      payment_method: paymentMethods.length > 0 ? paymentMethods[0].name : "Cash",
      transaction_id: "",
    });
  };

  const closePaymentModal = () => {
    setPayOpen(false);
  };


  const loadRemainingInvoicesByPatient = async (patientId) => {
    if (!patientId) {
      setRemainingInvoices([]);
      return;
    }
    try {
      const res = await axiosInstance.get(
        `getRemainingInvoices/${patientId}`
      );
      const list = Array.isArray(res.data?.invoices)
        ? res.data.invoices
        : Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
            ? res.data
            : [];
      setRemainingInvoices(list);
    } catch (_) {
      setRemainingInvoices([]);
    }
  };

  const handlePayPatientChange = async (e) => {
    const pid = e.target.value;
    setPayPatientId(pid);
    setPayForm((prev) => ({ ...prev, invoice_id: "", amount: "" }));
    await loadRemainingInvoicesByPatient(pid);
  };

  const handlePayPatientChangeSelect = async (selectedOption) => {
    const pid = selectedOption ? selectedOption.value : "";
    setPayPatientId(pid);
    setPayForm((prev) => ({ ...prev, invoice_id: "", amount: "" }));
    await loadRemainingInvoicesByPatient(pid);
  };

  const handlePayInvoiceSelect = (e) => {
    const idStr = e.target.value;
    const idNum = Number(idStr);
    const inv = remainingInvoices.find(
      (x) =>
        Number(x.invoiceId || x.invoice_id || x.id || x.invoiceid) === idNum ||
        (x.invoice_no || x.invoiceNo) === idStr
    );
    const remaining =
      inv?.remaining_amount ?? inv?.remaining ?? inv?.remainingAmount ?? "";
    setPayForm((prev) => ({
      ...prev,
      invoice_id: idStr,
      amount: remaining !== "" ? String(remaining) : prev.amount,
    }));
  };

  const submitPayment = async () => {
    if (!payPatientId) {
      Swal.fire("Patient required", "Please select a patient", "info");
      return;
    }
    if (!payForm.invoice_id || !payForm.amount) {
      Swal.fire("Missing", "Invoice and amount are required", "info");
      return;
    }
    try {
      setPayLoading(true);
      const payload = {
        invoice_id: Number(payForm.invoice_id),
        amount: Number(payForm.amount),
        payment_method: payForm.payment_method,
        transaction_id: payForm.transaction_id || undefined,
        branch_id: payBranchId || null,
      };
      await axiosInstance.post(`addPaymentInvoice`, payload);
      Swal.fire("Success", "Payment recorded successfully", "success");
      setPayOpen(false);
      setPayForm({
        invoice_id: "",
        amount: "",
        payment_method: "Cash",
        transaction_id: "",
      });
      setPayPatientId("");
      setRemainingInvoices([]);
      await Promise.all([loadInvoices(), loadPayments()]);
    } catch (e) {
      const msg = e.response?.data?.message || "Failed to add payment";
      Swal.fire("Error", msg, "error");
    } finally {
      setPayLoading(false);
    }
  };

  const exportPaymentsCSV = () => {
    const headers = ["#", "Patient", "Invoice ID", "Date", "Amount (BHD)", "Method", "Transaction", "Branch", "Cashier"];
    const rows = paymentsFiltered.map((p, idx) => {
      const d = p.payment_date || p.date || p.created_at;
      const dateStr = d ? new Date(d).toLocaleDateString("en-GB") : "-";
      const branch = allBranchSettings?.find(b => String(b.id) === String(p.branch_id));
      const branchName = branch ? (branch.official_name || `Branch ${branch.id}`) : (p.branch_id || "-");
      return [
        idx + 1,
        p.patient_name || "-",
        p.invoice_no || p.invoice_id || "-",
        dateStr,
        Number(p.payment_amount || p.amount || 0).toFixed(2),
        p.payment_method || p.method || "-",
        p.transactionId || p.transaction_id || "-",
        branchName,
        p.cashier_name || "-",
      ];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payments_${dayjs().format("YYYY-MM-DD")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleclick = async () => {
    setOpenmodal(true);
    setInvoiceBranchId(activeBranchId);
    if (!patients.length) {
      await loadPatients();
    }
  };

  const handleclose = () => {
    setOpenmodal(false);
    setSelectedPatientId("");
    setServices([]);
    setInventory([]);
    setSelected({ services: {}, inventory: {} });
    setServicesLoading(false);
    setInventoryLoading(false);
  };


  const openInsModal = () => setInsOpen(true);
  const closeInsModal = () => setInsOpen(false);
  const openInsListModal = async () => {
    setInsListOpen(true);
    await loadInsuranceCompanies();
  };
  const closeInsListModal = () => setInsListOpen(false);
  const loadInsuranceCompanies = async () => {
    try {
      setInsListLoading(true);
      const res = await axiosInstance.get(`getAllInsuranceCompanies`);
      const list = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
      setInsuranceCompanies(list);
    } catch (_) {
      setInsuranceCompanies([]);
    } finally {
      setInsListLoading(false);
    }
  };
  const handleInsChange = (e) => {
    const { name, value } = e.target;
    setInsForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleInsFile = (e) => {
    const file = e.target.files?.[0] || null;
    setInsForm((prev) => ({ ...prev, company_logo: file }));
  };
  const submitInsurance = async () => {
    if (!insForm.company_name) {
      Swal.fire("Required", "Company name is required", "info");
      return;
    }
    try {
      setInsLoading(true);
      const fd = new FormData();
      fd.append("company_name", insForm.company_name);
      fd.append("address", insForm.address);
      fd.append("phone_number", insForm.phone_number);
      fd.append("email", insForm.email);
      fd.append("website", insForm.website);
      if (insForm.company_logo) fd.append("company_logo", insForm.company_logo);
      const res = await axiosInstance.post(`createInsuranceCompany`, fd);
      const ok = res?.data?.success === true || res?.status < 400;
      Swal.fire(
        ok ? "Success" : "Notice",
        ok ? "Insurance company created successfully" : (res?.data?.message || "Request completed"),
        ok ? "success" : "info"
      );
      setInsOpen(false);
      if (insListOpen) {
        await loadInsuranceCompanies();
      }
      setInsForm({
        company_name: "",
        address: "",
        phone_number: "",
        email: "",
        website: "",
        company_logo: null,
      });
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "Failed to create company";
      console.error("createInsuranceCompany error", e);
      Swal.fire("Error", msg, "error");
    } finally {
      setInsLoading(false);
    }
  };

  const loadPatients = async () => {
    try {
      const res = await axiosInstance.get(`getActivePatients`);
      const list = Array.isArray(res.data?.data)
        ? res.data.data
        : Array.isArray(res.data)
          ? res.data
          : [];
      setPatients(list);
    } catch (_) {
      setPatients([]);
    }
  };

  const loadBillables = async (patientId) => {
    if (!patientId) return;
    try {
      setServicesLoading(true);
      setInventoryLoading(true);

      // fetch services
      axiosInstance
        .get(`getPatientServicesByPatientId/${patientId}`)
        .then((sRes) => {
          const rawServices = Array.isArray(sRes.data?.data)
            ? sRes.data.data
            : Array.isArray(sRes.data)
              ? sRes.data
              : Array.isArray(sRes.data?.data?.results)
                ? sRes.data.data.results
                : [];

          const eligibleServices = rawServices
            .map((x) => ({
              id: x.id || x.serviceId,
              name: x.name || x.serviceName,
              totalPrice:
                x.totalPrice ??
                x.total_price ??
                x.total ??
                Number(x.price || 0),
              status: (x.status || x.Status || "").toString().toLowerCase(),
              billed:
                x.billed ??
                x.isBilled ??
                x.is_billed ??
                ((x.billing_status || "").toString().toLowerCase() === "billed"
                  ? 1
                  : 0),
            }))
            .filter((x) => x.status === "complete" && String(x.billed) !== "1");

          setServices(eligibleServices);
        })
        .catch(() => setServices([]))
        .finally(() => setServicesLoading(false));


      axiosInstance
        .get(`getPatientPharmacyByPatientId/${patientId}`)
        .then((iRes) => {
          const rawInventory = (
            Array.isArray(iRes.data?.data)
              ? iRes.data.data
              : Array.isArray(iRes.data)
                ? iRes.data
                : []
          ).map((x) => ({
            id: x.id || x.itemId || x.pharmacyId,
            name: x.name,
            totalPrice:
              x.totalPrice ??
              x.total_price ??
              x.total ??
              Number(x.price || x.unitPrice || 0) * Number(x.quantity || 1),
            billed:
              x.billed ??
              x.isBilled ??
              x.is_billed ??
              ((x.billing_status || "").toString().toLowerCase() === "billed"
                ? 1
                : 0),
          }));

          const notBilledInventory = rawInventory.filter(
            (x) => String(x.billed) !== "1"
          );

          setInventory(notBilledInventory);
        })
        .catch(() => setInventory([]))
        .finally(() => setInventoryLoading(false));

      setSelected({ services: {}, inventory: {} });
    } catch (_) {
      setServices([]);
      setInventory([]);
      setServicesLoading(false);
      setInventoryLoading(false);
    }
  };

  const handlePatientChange = async (e) => {
    const pid = e.target.value;
    setSelectedPatientId(pid);
    await loadBillables(pid);
  };

  const handlePatientChangeSelect = async (selectedOption) => {
    const pid = selectedOption ? selectedOption.value : "";
    setSelectedPatientId(pid);
    await loadBillables(pid);
  };

  const toggleSelect = (type, id) => {
    setSelected((prev) => ({
      ...prev,
      [type]: { ...prev[type], [id]: !prev[type][id] },
    }));
  };

  const chosenServices = services.filter((x) => selected.services[x.id]);
  const chosenInventory = inventory.filter((x) => selected.inventory[x.id]);
  const selectedItemsTotalAmount = [...chosenServices, ...chosenInventory].reduce(
    (sum, x) => sum + Number(x.totalPrice || 0),
    0
  );

  const filterSearch = useMemo(() => {
    const q = search.toLowerCase();
    let rows = invoices || [];

    if (!q) return rows;
    return rows.filter((row) => {
      return (
        (row.invoice_no && row.invoice_no.toLowerCase().includes(q)) ||
        (row.patient_name && row.patient_name.toLowerCase().includes(q))
      );
    });
  }, [search, invoices]);

  // Invoice date/time filtering
  const filteredInvoices = useMemo(() => {
    let rows = filterSearch;

    // Build start/end as datetime if time is provided, otherwise date-only (day-level)
    const startStr = invoiceStartDate
      ? invoiceStartDate + (invoiceStartTime ? "T" + invoiceStartTime : "")
      : null;
    const endStr = invoiceEndDate
      ? invoiceEndDate + (invoiceEndTime ? "T" + invoiceEndTime : "")
      : null;

    const start = startStr ? dayjs(startStr) : null;
    const end = endStr ? dayjs(endStr) : null;
    const useTimeLevel = !!(invoiceStartTime || invoiceEndTime);

    if (start || end) {
      rows = rows.filter((invoice) => {
        const dStr = invoice.invoice_date || invoice.updated_at || invoice.updatedAt || invoice.date;
        if (!dStr) return false;
        const d = dayjs(dStr);
        if (!d.isValid()) return false;

        if (useTimeLevel) {
          // Minute-level comparison when time is involved
          if (start && d.isBefore(start, "minute")) return false;
          if (end && d.isAfter(end, "minute")) return false;
        } else {
          // Day-level comparison when only dates are set
          if (start && d.isBefore(start, "day")) return false;
          if (end && d.isAfter(end, "day")) return false;
        }

        return true;
      });
    }

    return rows;
  }, [filterSearch, invoiceStartDate, invoiceEndDate, invoiceStartTime, invoiceEndTime]);


  // Invoices pagination
  const totalPagesInvoices = Math.max(
    1,
    Math.ceil(filteredInvoices.length / itemsPerPage)
  );

  const paginatedInvoices = filteredInvoices.slice(
    (invoicePage - 1) * itemsPerPage,
    invoicePage * itemsPerPage
  );

  // Summary totals — auto-update with any active filter
  const summaryTotals = useMemo(() => {
    return filteredInvoices.reduce(
      (acc, inv) => {
        acc.totalInvoices  += Number(inv.total_amount     || 0);
        acc.totalPaid      += Number(inv.paid_amount      || 0);
        acc.totalRemaining += Number(inv.remaining_amount || 0);
        return acc;
      },
      { totalInvoices: 0, totalPaid: 0, totalRemaining: 0 }
    );
  }, [filteredInvoices]);

  // Unique cashier names derived from payments
  const cashierOptions = useMemo(() => {
    const names = new Set();
    payments.forEach((p) => {
      if (p.cashier_name) names.add(p.cashier_name);
    });
    return Array.from(names).sort();
  }, [payments]);

  // Payments filter + pagination
  const paymentsFiltered = useMemo(() => {
    const q = (paySearch || "").toLowerCase();
    const start = payStartDate ? dayjs(payStartDate) : null;
    const end = payEndDate ? dayjs(payEndDate) : null;

    let rows = payments;

    if (q) {
      rows = rows.filter((p) => {
        const patient = (p.patient_name || p.patientName || `${p.firstName || ""} ${p.lastName || ""}`.trim() || "").toLowerCase();
        const inv = String(p.invoice_no || p.invoice_id || p.invoiceId || "").toLowerCase();
        return patient.includes(q) || inv.includes(q);
      });
    }

    if (payCashierFilter) {
      rows = rows.filter((p) => p.cashier_name === payCashierFilter);
    }

    if (payBranchFilter) {
      rows = rows.filter((p) => String(p.branch_id) === payBranchFilter);
    }

    if (payMethodFilter) {
      rows = rows.filter((p) => (p.payment_method || p.method || "") === payMethodFilter);
    }

    if (start || end) {
      rows = rows.filter((p) => {
        const dStr = p.payment_date || p.date || p.created_at;
        if (!dStr) return false;
        const d = dayjs(dStr);
        if (!d.isValid()) return false;

        if (start && d.isBefore(start, "day")) return false;
        if (end && d.isAfter(end, "day")) return false;

        return true;
      });
    }

    return rows;
  }, [paySearch, payments, payStartDate, payEndDate, payCashierFilter, payBranchFilter, payMethodFilter]);

  const totalPagesPayments = Math.max(
    1,
    Math.ceil(paymentsFiltered.length / itemsPerPage)
  );
  const paginatedPayments = paymentsFiltered.slice(
    (paymentPage - 1) * itemsPerPage,
    paymentPage * itemsPerPage
  );

  const paymentSummary = useMemo(() => {
    let total = 0, cancelled = 0;
    paymentsFiltered.forEach(p => {
      const amt = Number(p.payment_amount || p.amount || 0);
      const rev = (p.payment_status || p.status || "").toLowerCase() === "reversed";
      if (rev) cancelled += amt;
      else total += amt;
    });
    return { total, cancelled };
  }, [paymentsFiltered]);

  const createInvoice = async () => {
    if (!selectedPatientId) {
      Swal.fire("Patient required", "Please select a patient", "info");
      return;
    }
    if (chosenServices.length === 0 && chosenInventory.length === 0) {
      Swal.fire(
        "Select items",
        "Please select at least one service or pharmacy item.",
        "info"
      );
      return;
    }
    try {
      setCreating(true);
      const payload = {
        patientId: Number(selectedPatientId),
        services: chosenServices.map((x) => x.id),
        pharmacy: chosenInventory.map((x) => x.id),
        branch_id: invoiceBranchId || null,
      };
      const res = await axiosInstance.post(`createInvoice`, payload);
      const created = res?.data?.data || null;
      Swal.fire(
        "Success",
        `Invoice ${created?.invoice_no || created?.invoiceNo || "created"
        } created successfully`,
        "success"
      );
      handleclose();
      // Notify globally that an invoice was created
      try {
        EventBus.dispatch("invoice:created", { patientId: Number(selectedPatientId), invoice: created });
      } catch (_) { }
      await loadInvoices();
    } catch (e) {
      const msg = e.response?.data?.message || "Failed to create invoice";
      Swal.fire("Error", msg, "error");
    } finally {
      setCreating(false);
    }
  };






  return (
    <div className="pc-container">
      <div className="pc-content ">
        <div>
          {/* Show loading while permissions are being checked */}
          {activeTab === null ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Header Buttons */}
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <button
                  onClick={() => navigate(-1)}
                  title="Back"
                  style={{ width: 30, height: 30, borderRadius: 8, border: '1px solid #e8ecf0', background: '#f8f9fb', color: '#5B6B79', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, marginRight: 4, flexShrink: 0 }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#4680ff22'; e.currentTarget.style.color = '#4680ff' }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#f8f9fb'; e.currentTarget.style.color = '#5B6B79' }}
                ><i className="ti ti-arrow-left" /></button>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: '#4680ff20', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="ti ti-file-invoice" style={{ color: '#4680ff', fontSize: 17 }} />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Billing</div>
                  <div style={{ fontSize: 11, color: '#5B6B79' }}>Manage invoices and payments</div>
                </div>
              </div>
              <ul className="nav nav-tabs mb-3" style={{ borderBottom: "2px solid #dee2e6" }}>
                <li className="nav-item">
                  <button
                    className={`nav-link${activeTab === "invoices" ? " active fw-semibold" : ""}`}
                    style={{
                      cursor: permissions.invoices ? "pointer" : "not-allowed",
                      opacity: permissions.invoices ? 1 : 0.5,
                      fontSize: 14,
                      padding: "8px 20px",
                      border: "none",
                      borderBottom: activeTab === "invoices" ? "3px solid #4a90e2" : "3px solid transparent",
                      color: activeTab === "invoices" ? "#4a90e2" : "#6c757d",
                      background: "transparent",
                    }}
                    onClick={() => permissions.invoices && setActiveTab("invoices")}
                  >
                    <i className="ti ti-file-invoice me-2"></i>Invoices
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link${activeTab === "payments" ? " active fw-semibold" : ""}`}
                    style={{
                      cursor: permissions.paymentInvoices ? "pointer" : "not-allowed",
                      opacity: permissions.paymentInvoices ? 1 : 0.5,
                      fontSize: 14,
                      padding: "8px 20px",
                      border: "none",
                      borderBottom: activeTab === "payments" ? "3px solid #4a90e2" : "3px solid transparent",
                      color: activeTab === "payments" ? "#4a90e2" : "#6c757d",
                      background: "transparent",
                    }}
                    onClick={() => permissions.paymentInvoices && setActiveTab("payments")}
                  >
                    <i className="ti ti-credit-card me-2"></i>Payments
                  </button>
                </li>
              </ul>

              {/* Tabs */}
              {activeTab === "invoices" && (
                <div className="col-12">
                  <div className="card table-card">
                    <div className="card-header bg-white p-3">
                      <div className="d-flex align-items-center justify-content-between gap-2" style={{ flexWrap: "nowrap" }}>
                        <div className="d-flex align-items-center gap-2" style={{ flexWrap: "nowrap", minWidth: 0 }}>

                          {/* Search */}
                          <div style={{ position: "relative", flexShrink: 0 }}>
                            <i
                              className="ti ti-search"
                              style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", zIndex: 2, color: "#6c757d", fontSize: "15px" }}
                            />
                            <input
                              type="text"
                              className="form-control m-0"
                              placeholder="Search patient or invoice"
                              value={search}
                              onChange={(e) => { setSearch(e.target.value); }}
                              style={{ paddingLeft: "34px", width: "200px" }}
                            />
                          </div>

                          {/* Date & Time filter — restricted to past 3 days */}
                          <div className="d-flex align-items-center gap-1" style={{ flexShrink: 0, flexWrap: "nowrap" }}>
                            <input type="date"
                              className="form-control m-0"
                              style={{ width: "140px" }}
                              value={invoiceStartDate}
                              min={dayjs().subtract(3, 'day').format('YYYY-MM-DD')}
                              max={dayjs().format('YYYY-MM-DD')}
                              onChange={(e) => {
                                const val = e.target.value;
                                const minDate = dayjs().subtract(3, 'day').format('YYYY-MM-DD');
                                const maxDate = dayjs().format('YYYY-MM-DD');
                                if (val < minDate || val > maxDate) return;
                                setInvoiceStartDate(val);
                                setInvoicePage(1);
                              }}
                              title="From Date (max 3 days back)"
                            />
                            <input type="time"
                              className="form-control m-0"
                              style={{ width: "110px" }}
                              value={invoiceStartTime}
                              onChange={(e) => { setInvoiceStartTime(e.target.value); setInvoicePage(1); }}
                              title="From Time"
                            />
                            <span className="text-muted px-1">–</span>
                            <input type="date"
                              className="form-control m-0"
                              style={{ width: "140px" }}
                              value={invoiceEndDate}
                              min={dayjs().subtract(3, 'day').format('YYYY-MM-DD')}
                              max={dayjs().format('YYYY-MM-DD')}
                              onChange={(e) => {
                                const val = e.target.value;
                                const minDate = dayjs().subtract(3, 'day').format('YYYY-MM-DD');
                                const maxDate = dayjs().format('YYYY-MM-DD');
                                if (val < minDate || val > maxDate) return;
                                setInvoiceEndDate(val);
                                setInvoicePage(1);
                              }}
                              title="To Date (max 3 days back)"
                            />
                            <input type="time"
                              className="form-control m-0"
                              style={{ width: "110px" }}
                              value={invoiceEndTime}
                              onChange={(e) => { setInvoiceEndTime(e.target.value); setInvoicePage(1); }}
                              title="To Time"
                            />
                            {(invoiceStartTime || invoiceEndTime) && (
                              <button
                                className="btn btn-sm btn-light text-danger"
                                style={{ flexShrink: 0 }}
                                onClick={() => {
                                  setInvoiceStartDate(dayjs().format('YYYY-MM-DD'));
                                  setInvoiceEndDate(dayjs().format('YYYY-MM-DD'));
                                  setInvoiceStartTime("");
                                  setInvoiceEndTime("");
                                  setInvoicePage(1);
                                }}
                                title="Reset to Today"
                              >
                                <i className="ti ti-x" />
                              </button>
                            )}
                          </div>

                        </div>

                        <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
                          <button className="btn btn-outline-primary btn-sm rounded" onClick={handleclick}>
                            Create Invoice
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* ── Summary Cards ─────────────────────────────────────── */}
                    <div className="row g-3 px-3 pt-3 pb-2">
                      {[
                        {
                          label: "Total Invoices",
                          value: summaryTotals.totalInvoices,
                          icon: "ti-file-invoice",
                          color: "#4680ff",
                          bg: "#f0f4ff",
                        },
                        {
                          label: "Paid",
                          value: summaryTotals.totalPaid,
                          icon: "ti-circle-check",
                          color: "#2ca87f",
                          bg: "#f0faf6",
                        },
                        {
                          label: "Remaining",
                          value: summaryTotals.totalRemaining,
                          icon: "ti-clock-dollar",
                          color: "#e58a00",
                          bg: "#fffbf0",
                        },
                      ].map(({ label, value, icon, color, bg }) => (
                        <div className="col-md-4" key={label}>
                          <div style={{
                            background: bg,
                            borderRadius: "12px",
                            padding: "16px 20px",
                            display: "flex",
                            alignItems: "center",
                            gap: "14px",
                            border: `1px solid ${color}22`,
                          }}>
                            <div style={{
                              width: "44px", height: "44px", borderRadius: "10px",
                              background: `${color}18`, display: "flex",
                              alignItems: "center", justifyContent: "center", flexShrink: 0,
                            }}>
                              <i className={`ti ${icon}`} style={{ fontSize: "22px", color }} />
                            </div>
                            <div>
                              <div style={{ fontSize: "11px", fontWeight: "600", color: "#9aa0ac", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                {label}
                              </div>
                              <div style={{ fontSize: "18px", fontWeight: "700", color: "#1a1f36", lineHeight: 1.3 }}>
                                {Number(value).toFixed(3)}
                                <span style={{ fontSize: "12px", fontWeight: "500", color: "#9aa0ac", marginLeft: "4px" }}>BHD</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover" id="pc-dt-simple">
                          <thead>
                            <tr>
                              <th>Sn</th>
                              <th>ID</th>
                              <th>Date</th>
                              <th>Patient </th>
                              {/* <th>Phone</th> */}
                              <th>Total Services</th>
                              <th>Amount</th>
                              {/* <th>Discount</th> */}
                              <th>VAT%</th>
                              <th>VAT Amount</th>
                              <th>Paid</th>
                              <th>Remaining Amount</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {loading ? (
                              <tr>
                                <td colSpan="11" className="text-center">
                                  <div
                                    className="spinner-border text-primary"
                                    role="status"
                                  >
                                    <span className="visually-hidden">
                                      Loading...
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ) : invoices.length === 0 ? (
                              <tr>
                                <td colSpan="11" className="text-center text-muted">
                                  No invoices found
                                </td>
                              </tr>
                            ) : (
                              paginatedInvoices.map((invoice, index) => (
                                <tr key={invoice.id || index}>
                                  <td>
                                    {(invoicePage - 1) * itemsPerPage + index + 1}
                                  </td>
                                  <td>{invoice.invoice_no || invoice.id}</td>
                                  <td>
                                    {(() => {
                                      const ts =
                                        invoice.invoice_date ||
                                        invoice.updated_at ||
                                        invoice.updatedAt ||
                                        invoice.date;
                                      const d = ts ? new Date(ts) : null;
                                      if (!d || isNaN(d.getTime())) return "-";
                                      const dateStr = d.toLocaleDateString("en-GB");
                                      const timeStr = d.toLocaleTimeString("en-US", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: true,
                                      });
                                      return (
                                        <span>
                                          {dateStr}<br />
                                          <small className="text-muted">{timeStr}</small>
                                        </span>
                                      );
                                    })()}
                                  </td>
                                  {/* <td>
                                    {invoice.patient_name ||
                                      `${invoice.firstName || ""} ${invoice.lastName || ""
                                        }`.trim()}
                                  </td> */}

                                  <td>
                                    <NavLink
                                      to="/Admin/Manageappointment"
                                      state={{ patientid: { id: invoice.patient_id } }}
                                      style={{ textDecoration: "none", color: "inherit" }}
                                    >
                                      {invoice.patient_name || `${invoice.firstName || ""} ${invoice.lastName || ""}`.trim()}
                                    </NavLink>
                                  </td>


                                  {/* <td>{invoice.phone || '-'} </td> */}
                                  <td>{invoice.total_services || "-"}</td>
                                  <td>
                                    {Number(invoice.total_amount || 0).toFixed(2)}{" "}
                                    BHD
                                  </td>
                                  {/* <td>{invoice.discount || '0'}</td> */}
                                  <td>
                                    {(() => {
                                      const vat = Number(invoice.total_vat_amount || 0);
                                      const base = Number(invoice.total_amount || 0) - vat;
                                      if (vat === 0 || base <= 0) return "0%";
                                      return `${((vat / base) * 100).toFixed(2)}%`;
                                    })()}
                                  </td>
                                  <td>
                                    {Number(invoice.total_vat_amount || 0).toFixed(3)} BHD
                                  </td>
                                  <td>
                                    {Number(invoice.paid_amount || 0).toFixed(2)}{" "}
                                    BHD
                                  </td>
                                  <td>
                                    {Number(invoice.remaining_amount || 0).toFixed(
                                      2
                                    )}{" "}
                                    BHD
                                  </td>
                                  <td>
                                    <div
                                      style={{ cursor: "pointer" }}
                                      className="avtar avtar-xs btn-link-secondary viewIcon"
                                      onClick={() =>
                                        navigate(`/Admin/ViewInvoice/${invoice.id || invoice.invoice_id || invoice.invoiceId || invoice.invoice_no}`,
                                          { state: { invoice } }
                                        )
                                      }
                                    >
                                      <i className="ti ti-eye f-20" />
                                      {/* <span>View< */}
                                    </div>
                                    {/* <div className="avtar avtar-xs btn-link-secondary editIcon" style={{ cursor: 'pointer' }}><i className="ti ti-edit f-20" /><span>Edit</span></div>
                              <div className="avtar avtar-xs btn-link-secondary deleteIcon" style={{ cursor: 'pointer' }}><i className="ti ti-trash f-20" /><span>Delete</span></div> */}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                        {totalPagesInvoices > 1 && (<div className="d-flex justify-content-end m-3">
                          <Pagination
                            currentPage={invoicePage}
                            totalPages={totalPagesInvoices}
                            setCurrentPage={setInvoicePage}
                          />
                        </div>)}

                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "payments" && (
                <div className="col-12">
                  <div className="card table-card">
                    <div className="card-header bg-white p-3">
                      <div className="d-flex flex-wrap align-items-center gap-2 justify-content-between">
                        {/* Left: all filters */}
                        <div className="d-flex flex-wrap align-items-center gap-2">

                          {/* Search */}
                          <div style={{ position: "relative", flexShrink: 0 }}>
                            <i className="ti ti-search" style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", zIndex: 2, color: "#6c757d", fontSize: "15px" }} />
                            <input
                              type="text"
                              className="form-control m-0"
                              placeholder="Search patient or invoice..."
                              value={paySearch}
                              onChange={(e) => { setPaySearch(e.target.value); setPaymentPage(1); }}
                              style={{ paddingLeft: "34px", width: "190px" }}
                            />
                          </div>

                          {/* Date range — restricted to past 3 days */}
                          <input
                            type="date"
                            className="form-control m-0"
                            style={{ width: "145px" }}
                            value={payStartDate}
                            min={dayjs().subtract(3, 'day').format('YYYY-MM-DD')}
                            max={dayjs().format('YYYY-MM-DD')}
                            onChange={(e) => {
                              const val = e.target.value;
                              const minDate = dayjs().subtract(3, 'day').format('YYYY-MM-DD');
                              const maxDate = dayjs().format('YYYY-MM-DD');
                              if (val < minDate || val > maxDate) return;
                              setPayStartDate(val);
                              setPaymentPage(1);
                            }}
                            title="From Date (max 3 days back)"
                          />
                          <span className="text-muted">–</span>
                          <input
                            type="date"
                            className="form-control m-0"
                            style={{ width: "145px" }}
                            value={payEndDate}
                            min={dayjs().subtract(3, 'day').format('YYYY-MM-DD')}
                            max={dayjs().format('YYYY-MM-DD')}
                            onChange={(e) => {
                              const val = e.target.value;
                              const minDate = dayjs().subtract(3, 'day').format('YYYY-MM-DD');
                              const maxDate = dayjs().format('YYYY-MM-DD');
                              if (val < minDate || val > maxDate) return;
                              setPayEndDate(val);
                              setPaymentPage(1);
                            }}
                            title="To Date (max 3 days back)"
                          />
                          {(payStartDate || payEndDate) && (
                            <button
                              className="btn btn-sm btn-light text-danger"
                              onClick={() => {
                                setPayStartDate(dayjs().format('YYYY-MM-DD'));
                                setPayEndDate(dayjs().format('YYYY-MM-DD'));
                                setPaymentPage(1);
                              }}
                              title="Reset to Today"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}

                          {/* Cashier */}
                          <select
                            className="form-select m-0"
                            style={{ width: "160px" }}
                            value={payCashierFilter}
                            onChange={(e) => { setPayCashierFilter(e.target.value); setPaymentPage(1); }}
                            title="Filter by Cashier"
                          >
                            <option value="">All Cashiers</option>
                            {cashierOptions.map((name) => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>

                          {/* Branch */}
                          <select
                            className="form-select m-0"
                            style={{ width: "150px" }}
                            value={payBranchFilter}
                            onChange={(e) => { setPayBranchFilter(e.target.value); setPaymentPage(1); }}
                            title="Filter by Branch"
                          >
                            <option value="">All Branches</option>
                            {(allBranchSettings || []).map((b, i) => (
                              <option key={b.id} value={String(b.id)}>
                                {b.official_name || `Branch ${i + 1}`}
                              </option>
                            ))}
                          </select>

                          {/* Payment Method */}
                          <select
                            className="form-select m-0"
                            style={{ width: "150px" }}
                            value={payMethodFilter}
                            onChange={(e) => { setPayMethodFilter(e.target.value); setPaymentPage(1); }}
                            title="Filter by Payment Method"
                          >
                            <option value="">All Methods</option>
                            {paymentMethods.map((m) => (
                              <option key={m.id} value={m.name}>{m.name}</option>
                            ))}
                          </select>

                        </div>

                        {/* Right: Export + Payment buttons */}
                        <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={exportPaymentsCSV}
                            title="Export to CSV"
                          >
                            <i className="ti ti-download me-1" />Export
                          </button>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={openPaymentModal}
                          >
                            Payment
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Summary cards */}
                    <div className="d-flex gap-3 px-3 pt-3">
                      <div style={{ flex: 1, background: '#f0faf5', border: '1px solid #b7e8d4', borderRadius: 10, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: '#2CA87F22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className="ti ti-wallet" style={{ fontSize: 20, color: '#2CA87F' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#5B6B79', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Payments</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#2CA87F', lineHeight: 1.2 }}>{paymentSummary.total.toFixed(2)} <span style={{ fontSize: 12, fontWeight: 500 }}>BHD</span></div>
                          <div style={{ fontSize: 11, color: '#5B6B79', marginTop: 2 }}>{paymentsFiltered.filter(p => (p.payment_status || p.status || "").toLowerCase() !== "reversed").length} payment(s)</div>
                        </div>
                      </div>
                      <div style={{ flex: 1, background: '#fff5f5', border: '1px solid #f5c6c6', borderRadius: 10, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: '#9E2B2518', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <i className="ti ti-arrow-back-up" style={{ fontSize: 20, color: '#9E2B25' }} />
                        </div>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#5B6B79', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Cancelled Payments</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: '#9E2B25', lineHeight: 1.2 }}>{paymentSummary.cancelled.toFixed(2)} <span style={{ fontSize: 12, fontWeight: 500 }}>BHD</span></div>
                          <div style={{ fontSize: 11, color: '#5B6B79', marginTop: 2 }}>{paymentsFiltered.filter(p => (p.payment_status || p.status || "").toLowerCase() === "reversed").length} reversed</div>
                        </div>
                      </div>
                    </div>

                    <div className="card-body pt-3">
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>Sn</th>
                              <th>Patient</th>
                              <th>Invoice ID</th>
                              <th>Date</th>
                              <th>Amount</th>
                              <th>Method</th>
                              <th>Transaction</th>
                              <th>Cashier</th>
                              <th>Status</th>
                              {/* <th>Status</th> */}
                            </tr>
                          </thead>
                          <tbody>
                            {paymentsLoading ? (
                              <tr>
                                <td colSpan="8" className="text-center">
                                  <div
                                    className="spinner-border text-primary"
                                    role="status"
                                  >
                                    <span className="visually-hidden">
                                      Loading...
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ) : paginatedPayments.length === 0 ? (
                              <tr>
                                <td colSpan="8" className="text-center text-muted">
                                  No payments found
                                </td>
                              </tr>
                            ) : (
                              paginatedPayments.map((p, idx) => {
                                const isReversed = (p.payment_status || p.status || "").toLowerCase() === "reversed";
                                return (
                                <tr key={p.id || idx} style={isReversed ? { background: '#fff5f5' } : {}}>
                                  <td>
                                    {(paymentPage - 1) * itemsPerPage + idx + 1}
                                  </td>
                                  <td>
                                    <NavLink
                                      to="/Admin/Manageappointment"
                                      state={{ patientid: { id: p.patient_id } }}
                                      style={{ textDecoration: "none", color: "inherit" }}
                                    >
                                      {p.patient_name || p.patientName || `${p.firstName || ""} ${p.lastName || ""}`.trim() || `Patient #${p.patient_id || "-"}`}
                                    </NavLink>
                                  </td>
                                  <td>
                                    {p.invoice_no || p.invoice_id || p.invoiceId}
                                  </td>
                                  <td>
                                    {(() => {
                                      const d =
                                        p.payment_date || p.date || p.created_at;
                                      return d
                                        ? new Date(d).toLocaleDateString("en-GB")
                                        : "-";
                                    })()}
                                  </td>
                                  <td style={isReversed ? { textDecoration: 'line-through', color: '#9E2B25' } : {}}>
                                    {Number(
                                      p.payment_amount || p.amount || 0
                                    ).toFixed(2)}{" "}
                                    BHD
                                  </td>
                                  <td>{p.payment_method || p.method || "-"}</td>
                                  <td>
                                    {p.transactionId || p.transaction_id || "-"}
                                  </td>
                                  <td>{p.cashier_name || "-"}</td>
                                  <td>
                                    {isReversed ? (
                                      <span style={{
                                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                                        background: '#fde8e8', color: '#9E2B25', letterSpacing: '0.05em',
                                        border: '1px solid #f5c6c6', whiteSpace: 'nowrap',
                                      }}>
                                        <i className="ti ti-arrow-back-up" style={{ fontSize: 10, marginRight: 3 }} />
                                        REVERSED
                                      </span>
                                    ) : (
                                      <span style={{
                                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                                        background: '#e6f9f1', color: '#2CA87F', letterSpacing: '0.05em',
                                        border: '1px solid #b7e8d4', whiteSpace: 'nowrap',
                                      }}>
                                        <i className="ti ti-circle-check" style={{ fontSize: 10, marginRight: 3 }} />
                                        COMPLETED
                                      </span>
                                    )}
                                  </td>
                                </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                        <div className="d-flex justify-content-end mt-3">
                          <Pagination
                            currentPage={paymentPage}
                            totalPages={totalPagesPayments}
                            setCurrentPage={setPaymentPage}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {openmodal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Invoice</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => handleclose()}
                ></button>
              </div>
              <div className="modal-body">
                {/* Patient selection */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Patient</label>
                    <Select
                      classNamePrefix="react-select"
                      name="patient_id"
                      options={patientOptions}
                      value={
                        patientOptions.find((opt) => opt.value === String(selectedPatientId)) || null
                      }
                      getOptionLabel={(e) => e.label}
                      getOptionValue={(e) => e.value}
                      onChange={handlePatientChangeSelect}
                      placeholder="Search by Name, Email, Mobile, Civil ID or Age"
                      isSearchable={true}
                      filterOption={customFilterOption}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                        menu: (base) => ({ ...base, zIndex: 2000 }),
                      }}
                      noOptionsMessage={() => (patients.length ? "No patients found" : "Loading patients...")}
                    />
                  </div>
                  {/* <div className="col-md-6 d-flex align-items-end"> */}
                  {/* <div className="w-100"> */}
                  {/* <div className="form-control bg-light"> */}
                  {/* <strong>Total:</strong> ₹{Number(totalAmount || 0).toFixed(2)} */}
                  {/* </div> */}
                  {/* </div> */}
                  {/* </div> */}
                </div>

                {/* Billable lists */}
                <div className="row">
                  <div className="col-md-6">
                    <h6>Services</h6>
                    <div
                      className="list-group"
                      style={{ maxHeight: 240, overflowY: "auto" }}
                    >
                      {servicesLoading ? (
                        <div className="list-group-item text-muted">Services loading...</div>
                      ) : services.length === 0 ? (
                        <div className="list-group-item text-muted">No billable services</div>
                      ) : (
                        services.map((s) => (
                          <label
                            key={s.id}
                            className={`list-group-item d-flex align-items-center ${selected.services[s.id] ? '' : ''}`}
                            style={{
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              // border: selected.services[s.id] ? '1px solid #0d6efd' : '1px solid transparent'
                            }}
                          >
                            <input
                              type="checkbox"
                              className="form-check-input me-2"
                              checked={!!selected.services[s.id]}
                              onChange={() => toggleSelect('services', s.id)}
                              style={{
                                accentColor: selected.services[s.id] ? '#0d6efd' : undefined,
                                transform: 'scale(1.1)',
                                cursor: 'pointer'
                              }}
                            />
                            <div className="flex-grow-1">
                              <div>
                                <strong className={selected.services[s.id] ? 'text-primary' : ''}>{s.name || `Service #${s.id}`}</strong>
                              </div>
                              <div className="text-muted small">
                                Total: {Number(s.totalPrice || 0).toFixed(2)} BHD
                              </div>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6>Pharmacy</h6>
                    <div
                      className="list-group"
                      style={{ maxHeight: 240, overflowY: "auto" }}
                    >
                      {inventoryLoading ? (
                        <div className="list-group-item text-muted">Pharmacy loading...</div>
                      ) : inventory.length === 0 ? (
                        <div className="list-group-item text-muted">No billable items</div>
                      ) : (
                        inventory.map((i) => (
                          <label
                            key={i.id}
                            className={`list-group-item d-flex align-items-center ${selected.inventory[i.id] ? '' : ''}`}
                            style={{
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              // border: selected.inventory[i.id] ? '1px solid #0d6efd' : '1px solid transparent'
                            }}
                          >
                            <input
                              type="checkbox"
                              className="form-check-input me-2"
                              checked={!!selected.inventory[i.id]}
                              onChange={() => toggleSelect('inventory', i.id)}
                              style={{
                                accentColor: selected.inventory[i.id] ? '#0d6efd' : undefined,
                                transform: 'scale(1.1)',
                                cursor: 'pointer'
                              }}
                            />
                            <div className="flex-grow-1">
                              <div>
                                <strong className={selected.inventory[i.id] ? 'text-primary' : ''}>{i.name || `Item #${i.id}`}</strong>
                              </div>
                              <div className="text-muted small">
                                Total: {Number(i.totalPrice || 0).toFixed(2)} BHD
                              </div>
                            </div>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {allBranchSettings && allBranchSettings.length > 1 && (
                <div className="px-3 pb-2">
                  <label className="form-label mb-1" style={{ fontSize: 13, fontWeight: 600 }}>Branch</label>
                  <select
                    className="form-select form-select-sm"
                    value={invoiceBranchId || ""}
                    onChange={(e) => setInvoiceBranchId(parseInt(e.target.value) || null)}
                  >
                    {allBranchSettings.map((b, i) => (
                      <option key={b.id} value={b.id}>
                        Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleclose}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={createInvoice}
                  disabled={creating}
                >
                  {creating ? "Saving..." : "Create Invoice"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment modal */}
      {payOpen && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Payment - {getLoggedInUserName()}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closePaymentModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Patient</label>
                  <Select
                    classNamePrefix="react-select"
                    name="pay_patient_id"
                    options={patientOptions}
                    value={
                      patientOptions.find((opt) => opt.value === String(payPatientId)) || null
                    }
                    getOptionLabel={(e) => e.label}
                    getOptionValue={(e) => e.value}
                    onChange={handlePayPatientChangeSelect}
                    placeholder="Search by Name, Email, Mobile, Civil ID or Age"
                    isSearchable={true}
                    filterOption={customFilterOption}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                      menu: (base) => ({ ...base, zIndex: 2000 }),
                    }}
                    noOptionsMessage={() => (patients.length ? "No patients found" : "Loading patients...")}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Invoice ID</label>
                  <select
                    className="form-select"
                    value={payForm.invoice_id}
                    onChange={handlePayInvoiceSelect}
                    disabled={!payPatientId}
                  >
                    <option value="">Select invoice</option>
                    {remainingInvoices.map((inv) => {
                      const id = String(
                        inv.invoiceId ||
                        inv.invoice_id ||
                        inv.id ||
                        inv.invoiceid
                      );
                      const labelNo = inv.invoice_no || inv.invoiceNo || id;
                      const remaining =
                        inv.remaining_amount ??
                        inv.remaining ??
                        inv.remainingAmount ??
                        0;
                      const dateStr =
                        inv.invoice_date ||
                        inv.updated_at ||
                        inv.updatedAt ||
                        inv.date;
                      const dateLabel = dateStr
                        ? new Date(dateStr).toLocaleDateString("en-GB")
                        : "-";
                      return (
                        <option
                          key={id}
                          value={id}
                        >{`${labelNo} - Rem: ${Number(remaining).toFixed(
                          2
                        )} - ${dateLabel}`}</option>
                      );
                    })}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    value={payForm.amount}
                    onChange={(e) =>
                      setPayForm({ ...payForm, amount: e.target.value })
                    }
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Payment Method</label>
                  <select
                    className="form-select"
                    value={payForm.payment_method}
                    onChange={(e) =>
                      setPayForm({ ...payForm, payment_method: e.target.value })
                    }
                  >
                    <option value="">Select payment method</option>
                    {paymentMethods.map((method) => (
                      <option key={method.id} value={method.name}>
                        {method.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Transaction ID (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={payForm.transaction_id}
                    onChange={(e) =>
                      setPayForm({ ...payForm, transaction_id: e.target.value })
                    }
                  />
                </div>





                {allBranchSettings && allBranchSettings.length > 1 && (
                  <div className="mb-3">
                    <label className="form-label">Branch</label>
                    <select
                      className="form-select"
                      value={payBranchId || ""}
                      onChange={(e) => setPayBranchId(parseInt(e.target.value) || null)}
                    >
                      {allBranchSettings.map((b, i) => (
                        <option key={b.id} value={b.id}>
                          Branch {i + 1}{b.official_name ? ` — ${b.official_name}` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={closePaymentModal}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={payLoading}
                  onClick={submitPayment}
                >
                  {payLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Insurance Company modal */}
      {insOpen && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Insurance Company</h5>
                <button type="button" className="btn-close" onClick={closeInsModal}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Company Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="company_name"
                    value={insForm.company_name}
                    onChange={handleInsChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    className="form-control"
                    name="address"
                    value={insForm.address}
                    onChange={handleInsChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="phone_number"
                    value={insForm.phone_number}
                    onChange={handleInsChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={insForm.email}
                    onChange={handleInsChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Website</label>
                  <input
                    type="text"
                    className="form-control"
                    name="website"
                    value={insForm.website}
                    onChange={handleInsChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Company Logo</label>
                  <input type="file" className="form-control" onChange={handleInsFile} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeInsModal}>Cancel</button>
                <button className="btn btn-primary" disabled={insLoading} onClick={submitInsurance}>
                  {insLoading ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Insurance Companies list modal */}
      {insListOpen && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Insurance Companies</h5>
                <button type="button" className="btn-close" onClick={closeInsListModal}></button>
              </div>
              <div className="modal-body">
                {insListLoading ? (
                  <div className="text-center text-muted">Loading...</div>
                ) : insuranceCompanies.length === 0 ? (
                  <div className="text-center text-muted">No companies found</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Sn</th>
                          <th>Name</th>
                          <th>Phone</th>
                          <th>Email</th>
                          <th>Website</th>
                        </tr>
                      </thead>
                      <tbody>
                        {insuranceCompanies.map((c, idx) => (
                          <tr key={c.id || idx}>
                            <td>{idx + 1}</td>
                            <td>{c.company_name || c.name}</td>
                            <td>{c.phone_number || c.phone || '-'}</td>
                            <td>{c.email || '-'}</td>
                            <td>{c.website || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeInsListModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Pagination({ currentPage, totalPages, setCurrentPage }) {
  return (
    <div className="d-flex align-items-center gap-3">
      <button
        className="btn btn-sm btn-outline-primary"
        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
      >
        {"<<"}
      </button>
      <span>
        {currentPage}/{totalPages}
      </span>
      <button
        className="btn btn-sm btn-outline-primary"
        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages}
      >
        {">>"}
      </button>
    </div>
  );
}
